import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record';

import { Box } from './Box';
import { DropdownMenu } from './DropdownMenu';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import { Typography } from './Typography';

interface AudioRecorderProps {
  trigger: React.ReactNode;
  onEnd: any; // replace with the actual type
  onStart?: any; // replace with the actual type
}

export const AudioRecorder = ({
  trigger,
  onEnd,
  onStart,
}: AudioRecorderProps) => {
  const waveformRef = useRef(null);
  const theme = useTheme();
  const [wavesurfer, setWavesurfer] = useState<any>(null);
  const [record, setRecord] = useState<any>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState('0:00');

  useEffect(() => {
    if (!wavesurfer) {
      const wavesurfer = WaveSurfer.create({
        container: document.createElement('div'),
        barWidth: 3,
        barHeight: 1.5,
        cursorWidth: 0,
        width: 100,
        height: 46,
        waveColor: theme.palette.primary.main,
        cursorColor: theme.palette.primary.lightest,
      });
      const record = wavesurfer.registerPlugin(RecordPlugin.create());
      record.on('record-start', () => {
        onStart();
      });
      record.on('record-end', (blob) => {
        onEnd(blob);
      });
      record.on('record-progress', () => {
        const totalMs = Number(record.getDuration() || 0);
        const totalSeconds = totalMs / 1000;
        const mins = Math.floor(totalSeconds / 60);
        const secs = Math.floor(totalSeconds % 60);
        const zeroPad = (num: number) => String(num).padStart(2, '0');
        setDuration(`${mins}:${zeroPad(secs)}`);
      });
      setWavesurfer(wavesurfer);
      setRecord(record);
    } else if (waveformRef.current) {
      wavesurfer.setOptions({ container: waveformRef.current });
    }
  }, [
    wavesurfer,
    onEnd,
    onStart,
    waveformRef,
    theme.palette.primary.ogSmart,
    theme.palette.primary.lightest,
    theme.palette.primary.main,
  ]);

  const handleTriggerClick = () => {
    if (record) {
      if (isRecording) {
        setOpen(false);
        record.stopRecording();
        setIsRecording(false);
      } else {
        setOpen(true);
        record.startRecording();
        setIsRecording(true);
      }
    }
  };

  const handleOpen = (open) => {
    if (!open) {
      handleTriggerClick();
    }
    setOpen(open);
  };

  return (
    <>
      <DropdownMenu
        trigger={<Box onClick={handleTriggerClick}>{trigger}</Box>}
        open={open}
        setOpen={handleOpen}
        closeOnContentClick={false}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Icon
            sx={{ mx: 2 }}
            icon={icon({ name: 'microphone', style: 'regular' })}
          />
          <Box ref={waveformRef} width={100} height={46} />
          <Typography
            variant="body2"
            sx={{ px: 1, width: 50, textAlign: 'center' }}
          >
            {duration}
          </Typography>
          <IconButton
            onClick={handleTriggerClick}
            size="small"
            sx={{
              mr: 1,
              borderRadius: '50%',
              bgcolor: 'primary.lightest',
              '&:hover': {
                bgcolor: 'primary.200',
              },
            }}
          >
            <Icon
              icon={icon({ name: 'stop', style: 'solid' })}
              color="primary"
              size="xsmall"
            />
          </IconButton>
        </Box>
      </DropdownMenu>
    </>
  );
};
